<template>
  <div>
    <div v-if="loading">
      <v-skeleton-loader type="button"></v-skeleton-loader>
    </div>
    <div v-else-if="!user">
      <v-btn color="primary">Login to Sign Up</v-btn>
    </div>
    <div v-else>
      <v-btn
        v-if="userSignedUp"
        @click="drop"
        color="error"
        text
        rounded
        class="mr-5"
        :disabled="isPast"
        :loading="btnLoading"
      >Drop from session</v-btn>
      <v-btn
        v-if="userSignedUp"
        :loading="btnLoading"
        :href="calendarLink"
        rounded
        target="_blank"
      >Add to Calendar</v-btn>
      <v-btn
        v-else
        @click="signup" 
        color="primary"
        rounded
        :disabled="signUpDisabled"
        :loading="btnLoading"
      >{{ signUpButtonText }}</v-btn>
    </div>
  </div>
</template>

<script>
  import moment from 'moment'
  import User from '@/mixins/User'
  
  export default {
    name: 'SessionSignUpButton',
    props: {
      session: Object
    },
    mixins: [ User ],
    data () {
      return {
        btnLoading: false
      }
    },
    computed: {
      calendarLink () {
        const title = this.session.fields.Name
        const description = this.session.fields.Description || ''
        const location = this.session.fields.Location || ''
        const datesString = moment.utc(this.session.fields.Start)
                            .format().replace(/:|-/g,'') + '%2F' +
                            moment.utc(this.session.fields.End)
                            .format().replace(/:|-/g,'')
        const videoLink = this.session.fields['Video Share Link'] || false
        let details = ''
        if (videoLink) {
          // the new lines in this variable are intentional
          details = `Video Link: ${videoLink}


`
        }
        details += description
        
        return 'https://www.google.com/calendar/render?action=TEMPLATE' + 
               '&dates=' + datesString +
               '&text=' + encodeURIComponent(title) +
               '&details=' + encodeURIComponent(details) +
               '&location=' + encodeURIComponent(location)
      },
      loading () {
        return !this.user || !this.session
      },
      userSignedUp () {
        if (!this.userEmployeeRecord) { return false }
        const attendees = this.session.fields.Attendees || []
        return attendees.includes(this.userEmployeeRecord.id)
      },
      isAtCapacity () {
        const capacityLimit = this.session.fields.Capacity
        const numAttendees = this.session.fields['Attendee Count']
        return capacityLimit ? numAttendees >= capacityLimit : false
      },
      isPast () {
        return moment(this.session.fields.Start).isBefore(new Date())
      },
      isTBD () {
        return !this.session.fields.Start || !this.session.fields.End
      },
      signUpDisabled () {
        return this.isPast || this.isAtCapacity || 
               this.isTBD || !this.userEmployeeRecord
      },
      signUpButtonText () {
        if (this.isPast) {
          return 'Session is in the past'
        }
        if (this.isAtCapacity) {
          return 'Session is full'
        }
        if (this.isTBD) {
          return 'Session schedule is TBD'
        }
        return 'Sign up'
      }
    },
    methods: {
      async drop () {
        if (!this.user.customMeta) {
          return this.$store.dispatch('setSnackbar', {
            snackbarColor: 'error',
            showSnackbar: true,
            snackbarY: 'top',
            snackbarX: 'right',
            snackbarText: 'There are problems dropping you from this session.'
          })
        }
        if (!this.userEmployeeRecord) {
          return this.$store.dispatch('setSnackbar', {
            snackbarColor: 'error',
            showSnackbar: true,
            snackbarY: 'top',
            snackbarX: 'right',
            snackbarText: `There are problems dropping you from this session:
                           No employee found with email: ${this.user.email}`
          })
        }
        this.btnLoading = true
        
        let employeeSessions = this.userEmployeeRecord.fields.Sessions
        if (employeeSessions) {
          employeeSessions = employeeSessions.filter(s => {
            return s !== this.session.id
          })
        } else {
          employeeSessions = []
        }
        const fields = {
          'Sessions': employeeSessions
        }
        this.$store.dispatch('updateRecords', {
          table: 'Employees',
          records: [{ id: this.userEmployeeRecord.id, fields: fields }]
        })
        .then(async () => {
          await this.$store.dispatch('getRecords', {
            table: 'Sessions',
            view: 'All Sessions',
            dispatch: 'setSessions'
          })
          this.btnLoading = false
          this.$store.dispatch('setUserEmployeeRecord')
          return this.$store.dispatch('setSnackbar', {
            snackbarColor: 'success',
            showSnackbar: true,
            snackbarY: 'top',
            snackbarX: 'right',
            snackbarText: 'You have successfully dropped from the session!'
          })
        })
      },
      async signup () {
        if (!this.user.customMeta) {
          return this.$store.dispatch('setSnackbar', {
            snackbarColor: 'error',
            showSnackbar: true,
            snackbarY: 'top',
            snackbarX: 'right',
            snackbarText: 'There are problems signing you up for this session.'
          })
        }
        if (!this.userEmployeeRecord) {
          return this.$store.dispatch('setSnackbar', {
            snackbarColor: 'error',
            showSnackbar: true,
            snackbarY: 'top',
            snackbarX: 'right',
            snackbarText: `There are problems signing you up for this session:
                           No employee found with email: ${this.user.email}`
          })
        }
        this.btnLoading = true

        let employeeSessions = this.userEmployeeRecord.fields.Sessions
        if (employeeSessions) {
          employeeSessions.push(this.session.id)
        } else {
          employeeSessions = [this.session.id]
        }
        
        const fields = {
          'Sessions': employeeSessions
        }
        this.$store.dispatch('updateRecords', {
          table: 'Employees',
          records: [{ id: this.userEmployeeRecord.id, fields: fields }]
        })
        .then(async () => {
          await this.$store.dispatch('getRecords', {
            table: 'Sessions',
            view: 'All Sessions',
            dispatch: 'setSessions'
          })
          this.btnLoading = false
          this.$store.dispatch('setUserEmployeeRecord')
          this.$store.dispatch('sendEmail', {
            to: this.user.email,
            bcc: this.user.email,
            subject: `You're signed up for ${this.session.fields.Name}!`,
            body: `Don't forget to <a href="${this.calendarLink}">add this event to your calendar</a>.
                   You can also <a href="${window.location.href}">manage your schedule</a> in case you need to switch sessions.`
          })
          return this.$store.dispatch('setSnackbar', {
            snackbarColor: 'success',
            showSnackbar: true,
            snackbarY: 'top',
            snackbarX: 'right',
            snackbarText: 'You have successfully signed up!'
          })
        })
      }
    }
  }
</script>
